import { CommonModule, DatePipe, NgForOf, NgIf } from "@angular/common";
import { Component, inject, OnInit } from "@angular/core";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import {
  TuiAlertService,
  TuiAppearance,
  TuiButton,
  TuiDataList,
  TuiDropdown,
  TuiLink,
  TuiLoader,
  tuiLoaderOptionsProvider,
  TuiTextfield,
} from "@taiga-ui/core";
import {
  TuiBreadcrumbs,
  TuiDataListDropdownManager,
  TuiDataListWrapper,
  TuiFade,
  TuiPagination,
  TuiStringifyContentPipe,
} from "@taiga-ui/kit";
import { TuiCell, TuiNavigation, TuiSearch } from "@taiga-ui/layout";
import { TuiTable } from "@taiga-ui/addon-table";
import {
  TuiComboBoxModule,
  TuiTextfieldControllerModule,
} from "@taiga-ui/legacy";
import { AuditActorType, AuditLogService } from "../../core/services/audit-log.service";
import { AdminService } from "../../core/services/admin.service";
import { NurseService } from "../../core/services/nurse.service";
import { ClientService } from "../../core/services/client.service";
import { loadListState, saveListState } from "../../shared/utils/list-state";
import { AuthService } from "../../core/services/auth.service";
import { formatClientIdentifier } from "../../shared/utils/client-identifier";

type StaffOption = {
  label: string;
  actorType: AuditActorType;
  actorId: string | null;
};

type ClientOption = { label: string; id: string };

@Component({
  selector: "app-logs",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    NgIf,
    NgForOf,
    TuiAppearance,
    TuiBreadcrumbs,
    TuiButton,
    TuiCell,
    TuiComboBoxModule,
    TuiDataList,
    TuiDataListDropdownManager,
    TuiDataListWrapper,
    TuiDropdown,
    TuiFade,
    TuiLink,
    TuiLoader,
    TuiNavigation,
    TuiPagination,
    TuiSearch,
    TuiStringifyContentPipe,
    TuiTable,
    TuiTextfield,
    TuiTextfieldControllerModule,
  ],
  templateUrl: "./logs.component.html",
  styleUrl: "./logs.component.scss",
  providers: [DatePipe, tuiLoaderOptionsProvider({ size: "xl" })],
})
export class LogsComponent implements OnInit {
  protected readonly breadcrumbs = ["Royal Hospital", "Logs"];
  protected readonly sizes = ["m"] as const;
  protected size = this.sizes[0];

  private auditLogService = inject(AuditLogService);
  private adminService = inject(AdminService);
  private nurseService = inject(NurseService);
  private clientService = inject(ClientService);
  private alerts = inject(TuiAlertService);
  private authService = inject(AuthService);

  isLoading = false;
  logs: any[] = [];
  total = 0;
  pageIndex = 1;
  pageSize = 10;
  sortOrder: 1 | -1 = -1;

  clientOptions: ClientOption[] = [];
  staffOptions: StaffOption[] = [{ label: "System", actorType: "System", actorId: null }];

  selectedPatient: ClientOption | null = null;
  selectedStaff: StaffOption | null = null;
  fromDate: string | null = null; // yyyy-mm-dd
  toDate: string | null = null; // yyyy-mm-dd
  canPurgeAuditLogs = false;

  protected stringifyClient = (item: ClientOption | null): string => (item ? item.label : "");
  protected stringifyStaff = (item: StaffOption | null): string => (item ? item.label : "");

  private readonly listStateKey = "logs-list";

  ngOnInit(): void {
    this.checkPurgePermission();
    this.restoreListState();
    this.loadFilterOptions();
    this.loadLogs();
  }

  private checkPurgePermission(): void {
    this.canPurgeAuditLogs = this.authService.hasPermission("AUDIT_LOG_PURGE");
  }

  private restoreListState(): void {
    const restored = loadListState(this.listStateKey, {
      pageIndex: this.pageIndex,
      pageSize: this.pageSize,
      sortOrder: this.sortOrder,
      selectedPatient: this.selectedPatient,
      selectedStaff: this.selectedStaff,
      fromDate: this.fromDate,
      toDate: this.toDate,
    });
    this.pageIndex = restored.pageIndex;
    this.pageSize = restored.pageSize;
    this.sortOrder = restored.sortOrder;
    this.selectedPatient = restored.selectedPatient;
    this.selectedStaff = restored.selectedStaff;
    this.fromDate = restored.fromDate;
    this.toDate = restored.toDate;
  }

  private rememberListState(): void {
    saveListState(this.listStateKey, {
      pageIndex: this.pageIndex,
      pageSize: this.pageSize,
      sortOrder: this.sortOrder,
      selectedPatient: this.selectedPatient,
      selectedStaff: this.selectedStaff,
      fromDate: this.fromDate,
      toDate: this.toDate,
    });
  }

  loadFilterOptions(): void {
    this.clientService.getAllClients().subscribe({
      next: (clients: any) => {
        this.clientOptions = (clients || []).map((c: any) => ({
          id: c._id,
          label: `${formatClientIdentifier(c)} ${c.name || ""}`.trim(),
        }));

        const clientActorOptions = (clients || []).map((c: any) => ({
          label: `Client: ${formatClientIdentifier(c)} ${(c.guardianName || c.name || "").toString()}`.trim(),
          actorType: "Client" as const,
          actorId: c._id,
        }));
        this.staffOptions = [...this.staffOptions, ...clientActorOptions];
      },
    });

    this.adminService.getAllUsers().subscribe({
      next: (users: any) => {
        const options = (users || []).map((u: any) => ({
          label: `Admin: ${(u.refNo || "").toString()} ${(u.name || "").toString()}`.trim(),
          actorType: "User" as const,
          actorId: u._id,
        }));
        this.staffOptions = [...this.staffOptions, ...options];
      },
    });

    this.nurseService.getAllNurses().subscribe({
      next: (nurses: any) => {
        const options = (nurses || []).map((n: any) => ({
          label: `Nurse: ${(n.refNo || "").toString()} ${(n.name || "").toString()}`.trim(),
          actorType: "Nurse" as const,
          actorId: n._id,
        }));
        this.staffOptions = [...this.staffOptions, ...options];
      },
    });
  }

  private toIsoDayStart(dateStr: string | null): string | null {
    if (!dateStr) return null;
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return null;
    d.setHours(0, 0, 0, 0);
    return d.toISOString();
  }

  private toIsoDayEnd(dateStr: string | null): string | null {
    if (!dateStr) return null;
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return null;
    d.setHours(23, 59, 59, 999);
    return d.toISOString();
  }

  loadLogs(): void {
    this.rememberListState();
    const filters: any = {};

    if (this.selectedPatient?.id) {
      filters.targetId = this.selectedPatient.id;
    }

    if (this.selectedStaff?.actorType === "System") {
      filters.systemOnly = true;
    } else if (this.selectedStaff?.actorId) {
      filters.actorType = this.selectedStaff.actorType;
      filters.actorId = this.selectedStaff.actorId;
    }

    const fromIso = this.toIsoDayStart(this.fromDate);
    const toIso = this.toIsoDayEnd(this.toDate);
    if (fromIso) filters.from = fromIso;
    if (toIso) filters.to = toIso;

    this.isLoading = true;
    this.auditLogService
      .getPagedAuditLogs({
        pageIndex: this.pageIndex,
        pageSize: this.pageSize,
        sortOrder: this.sortOrder,
        filters,
      })
      .subscribe({
        next: (res: any) => {
          const first = Array.isArray(res) ? res[0] : null;
          this.logs = first?.data || [];
          this.total = first?.metadata?.[0]?.total || 0;
        },
        error: (error) => {
          console.error("Error fetching audit logs:", error);
          this.alerts
            .open("Failed to load logs.", { label: "Error", appearance: "error" })
            .subscribe();
        },
        complete: () => (this.isLoading = false),
      });
  }

  applyFilters(): void {
    this.pageIndex = 1;
    this.loadLogs();
  }

  clearFilters(): void {
    this.selectedPatient = null;
    this.selectedStaff = null;
    this.fromDate = null;
    this.toDate = null;
    this.applyFilters();
  }

  onPageChange(pageIndex: number): void {
    this.pageIndex = pageIndex;
    this.loadLogs();
  }

  getPageCount(): number {
    return Math.ceil(this.total / this.pageSize);
  }

  clearOldLogs(range: "6m" | "1y"): void {
    const now = new Date();
    const olderThan = new Date(now);
    if (range === "6m") olderThan.setMonth(olderThan.getMonth() - 6);
    if (range === "1y") olderThan.setFullYear(olderThan.getFullYear() - 1);

    this.auditLogService.clearOldLogs(olderThan.toISOString()).subscribe({
      next: (res: any) => {
        const deleted = res?.deletedCount ?? 0;
        this.alerts
          .open(`Deleted ${deleted} log(s).`, { label: "Success", appearance: "success" })
          .subscribe();
        this.loadLogs();
      },
      error: () => {
        this.alerts
          .open("Failed to clear old logs.", { label: "Error", appearance: "error" })
          .subscribe();
      },
    });
  }
}

